import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import SkeletonImage from '../common/SkeletonImage';

const VideoSlide = ({ src, onLoadedMetadata }) => {
  const [ready, setReady] = useState(false);

  return (
    <>
      {!ready && (
        <div className="absolute inset-0 z-10 bg-slate-300/40 backdrop-blur-[2px]">
          <div className="absolute inset-0 shimmer-animation" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white/15 backdrop-blur-md border border-white/25 animate-pulse">
              <svg className="w-7 h-7 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86a1 1 0 0 0-1.5.86z" />
              </svg>
            </div>
          </div>
        </div>
      )}
      <video
        src={src}
        autoPlay
        muted
        loop
        playsInline
        className={`w-full h-full object-cover transition-opacity duration-700 ${ready ? 'opacity-100' : 'opacity-0'}`}
        onLoadedMetadata={onLoadedMetadata}
        onCanPlay={() => setReady(true)}
        onPlaying={() => setReady(true)}
        onError={() => setReady(true)}
      />
    </>
  );
};

const BannerCarousel = ({
  banners = [],
  apiUrl = '',
  scrollTargetId = '',
  scrollOffsetPx = 0,
  scrollFallbackPx = 0
}) => {
  const PRIMARY = '#16A34A';
  const TRANSITION_MS = 700;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [direction, setDirection] = useState(1);
  const [progress, setProgress] = useState(0);
  const [slideDuration, setSlideDuration] = useState(6600);
  const [videoDurations, setVideoDurations] = useState({});
  const intl = useIntl();
  const transitionTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    };
  }, []);

  const displayBanners = banners;
  const hasMultipleSlides = displayBanners.length > 1;

  const getBannerKey = (banner, index) =>
    banner?.id ?? banner?.documentId ?? banner?.slug ?? banner?.image ?? index;

  const startTransitionTo = (nextIndex, dir = 1) => {
    if (!hasMultipleSlides) {
      setCurrentIndex(nextIndex);
      return;
    }
    if (nextIndex === currentIndex) return;

    setProgress(0);
    setDirection(dir);
    setPrevIndex(currentIndex);
    setCurrentIndex(nextIndex);

    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }

    transitionTimeoutRef.current = window.setTimeout(() => {
      setPrevIndex(null);
    }, TRANSITION_MS);
  };

  const prevSlide = () => {
    if (hasMultipleSlides) {
      const nextIndex = currentIndex === 0 ? displayBanners.length - 1 : currentIndex - 1;
      startTransitionTo(nextIndex, -1);
    }
  };

  const nextSlide = () => {
    if (hasMultipleSlides) {
      const nextIndex = currentIndex === displayBanners.length - 1 ? 0 : currentIndex + 1;
      startTransitionTo(nextIndex, 1);
    }
  };

  const goToSlide = (index) => {
    if (hasMultipleSlides) {
      if (index === currentIndex) return;
      const dir = index > currentIndex ? 1 : -1;
      startTransitionTo(index, dir);
    }
  };

  useEffect(() => {
    const currentBanner = displayBanners[currentIndex];
    if (currentBanner) {
      const isVideo = currentBanner?.image && /\.(mp4|webm|ogg)$/i.test(currentBanner.image);
      const bannerKey = currentBanner?.id ?? currentBanner?.documentId ?? currentBanner?.slug ?? currentIndex;

      if (isVideo && videoDurations[bannerKey]) {
        setSlideDuration(videoDurations[bannerKey] * 1000);
      } else {
        setSlideDuration(6600);
      }
    }
  }, [currentIndex, displayBanners, videoDurations]);

  const handleVideoLoaded = (banner, duration) => {
    const bannerKey = banner?.id ?? banner?.documentId ?? banner?.slug ?? currentIndex;
    setVideoDurations((prev) => ({
      ...prev,
      [bannerKey]: duration
    }));
  };

  useEffect(() => {
    if (!hasMultipleSlides) return;
    setProgress(0);

    const safeDuration = Math.max(2000, slideDuration || 0);
    const intervalTime = 100;
    const increment = 100 / (safeDuration / intervalTime);

    const id = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          return 0;
        }
        const next = prev + increment;
        if (next >= 100) {
          const nextIndex = currentIndex === displayBanners.length - 1 ? 0 : currentIndex + 1;
          startTransitionTo(nextIndex, 1);
          return 0;
        }
        return next;
      });
    }, intervalTime);

    return () => window.clearInterval(id);
  }, [currentIndex, displayBanners.length, hasMultipleSlides, slideDuration]);

  useEffect(() => {
    if (currentIndex >= displayBanners.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex, displayBanners.length]);

  useEffect(() => {
    if (prevIndex != null && prevIndex >= displayBanners.length) {
      setPrevIndex(null);
    }
  }, [displayBanners.length, prevIndex]);

  useEffect(() => {
    if (!hasMultipleSlides) setProgress(0);
  }, [hasMultipleSlides]);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-hytera-carousel-styles', '');
    style.textContent = `
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(14px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes bhSlideEnterFromLeft {
        from { transform: translate3d(-100%, 0, 0); }
        to { transform: translate3d(0, 0, 0); }
      }
      @keyframes bhSlideExitToRight {
        from { transform: translate3d(0, 0, 0); }
        to { transform: translate3d(100%, 0, 0); }
      }
      @keyframes bhSlideEnterFromRight {
        from { transform: translate3d(100%, 0, 0); }
        to { transform: translate3d(0, 0, 0); }
      }
      @keyframes bhSlideExitToLeft {
        from { transform: translate3d(0, 0, 0); }
        to { transform: translate3d(-100%, 0, 0); }
      }
      .animate-fade-in-up {
        animation: fadeInUp 1.1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        opacity: 0;
      }
      .delay-100 { animation-delay: 0.1s; }
      .delay-200 { animation-delay: 0.22s; }
      .delay-300 { animation-delay: 0.34s; }
      .delay-400 { animation-delay: 0.46s; }
    `;
    document.head.appendChild(style);
    return () => {
      if (style && style.isConnected) {
        style.parentNode?.removeChild(style);
      }
    };
  }, []);

  const activeBanner = useMemo(() => displayBanners[currentIndex], [displayBanners, currentIndex]);
  const activeBannerKey = useMemo(
    () => getBannerKey(activeBanner, currentIndex),
    [activeBanner, currentIndex]
  );

  const renderBannerContent = (banner) => {
    const hasLink = typeof banner?.link === 'string' ? banner.link.trim().length > 0 : Boolean(banner?.link);

    const handleExplore = (e) => {
      e?.preventDefault?.();
      if (scrollTargetId) {
        const el = document.getElementById(scrollTargetId);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - (Number(scrollOffsetPx) || 0);
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          return;
        }
      }
      const fallback = Number(scrollFallbackPx) || window.innerHeight;
      const top = window.scrollY + fallback - (Number(scrollOffsetPx) || 0);
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };

    const isVideo = banner?.image && /\.(mp4|webm|ogg)$/i.test(banner.image);
    const videoSrc =
      apiUrl && banner.image && banner.image.startsWith('/')
        ? `${apiUrl}${banner.image}`
        : banner.image;
    const imageSrc =
      apiUrl && banner.image && banner.image.startsWith('/')
        ? `${apiUrl}${banner.image}`
        : banner.image;

    return (
      <div className="relative w-full h-full overflow-hidden">
        <div key={isVideo ? videoSrc : imageSrc} className="absolute inset-0">
          {isVideo ? (
            <VideoSlide
              src={videoSrc}
              onLoadedMetadata={(e) => handleVideoLoaded(banner, e.target.duration)}
            />
          ) : (
            <SkeletonImage
              src={imageSrc}
              alt={banner?.alt || banner?.title || ''}
              className="w-full h-full"
              imgClassName="w-full h-full object-cover scale-[1.02]"
              icon={false}
            />
          )}
          <div className="absolute inset-0 bg-[#0a1628]/55" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/90 via-[#0a1628]/55 to-transparent" />
          <div className="absolute inset-y-0 left-0 w-1 bg-[#16A34A]" />
        </div>

        <div className="absolute inset-0 flex items-end sm:items-center pb-36 sm:pb-0">
          <div className="w-full max-w-[1400px] mx-auto px-5 sm:px-8 md:px-12 lg:px-16">
            <div className="max-w-3xl">
              <p className="text-[#16A34A] text-[11px] sm:text-xs font-semibold tracking-[0.28em] uppercase mb-5 sm:mb-7 animate-fade-in-up">
                Africa Systems
              </p>
              <h1 className="text-white font-bold leading-[0.95] tracking-tight text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem] animate-fade-in-up delay-100">
                {banner?.title}
              </h1>
              <div className="mt-6 sm:mt-8 flex gap-5 items-start animate-fade-in-up delay-200">
                <span className="mt-2 hidden sm:block w-10 h-px bg-[#16A34A] shrink-0" />
                <p className="text-white/80 text-sm sm:text-base md:text-lg leading-relaxed max-w-xl">
                  {banner?.description}
                </p>
              </div>

              <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4 sm:gap-6 animate-fade-in-up delay-300">
                {hasLink ? (
                  <a
                    href={banner.link}
                    className="group inline-flex items-center gap-3 bg-[#16A34A] text-white px-6 py-3.5 text-xs sm:text-sm font-semibold tracking-[0.14em] uppercase transition-colors duration-200 hover:bg-[#15803D]"
                    aria-label={intl.formatMessage({ id: 'learn.more', defaultMessage: 'Learn more' })}
                  >
                    {intl.formatMessage({ id: 'learn.more', defaultMessage: 'Learn more' })}
                    <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={handleExplore}
                    className="group inline-flex items-center gap-3 bg-[#16A34A] text-white px-6 py-3.5 text-xs sm:text-sm font-semibold tracking-[0.14em] uppercase transition-colors duration-200 hover:bg-[#15803D]"
                    aria-label={intl.formatMessage({ id: 'explore', defaultMessage: 'Explore' })}
                  >
                    {intl.formatMessage({ id: 'explore', defaultMessage: 'Explorer' })}
                    <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m0 0l-5-5m5 5l5-5" />
                    </svg>
                  </button>
                )}

                {hasMultipleSlides ? (
                  <div className="flex items-center gap-2 ml-auto sm:ml-0">
                    <button
                      onClick={prevSlide}
                      className="w-11 h-11 grid place-items-center border border-white/25 text-white/80 hover:border-white hover:text-white transition-colors"
                      aria-label="Previous slide"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={nextSlide}
                      className="w-11 h-11 grid place-items-center border border-white/25 text-white hover:border-[#16A34A] hover:bg-[#16A34A] transition-colors"
                      aria-label="Next slide"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <span className="ml-3 text-white/50 text-xs tracking-[0.2em] tabular-nums">
                      {String(currentIndex + 1).padStart(2, '0')}
                      <span className="mx-1.5 text-white/30">/</span>
                      {String(displayBanners.length).padStart(2, '0')}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative w-full h-[88vh] min-h-[520px] sm:min-h-[600px] md:min-h-[680px] max-h-[920px] overflow-hidden bg-[#0a1628]">
      {activeBanner ? (
        <>
          {prevIndex != null && displayBanners[prevIndex] ? (
            <div
              key={`prev-${getBannerKey(displayBanners[prevIndex], prevIndex)}`}
              className="absolute inset-0 z-10 pointer-events-none"
              style={{
                willChange: 'transform',
                animation: `${direction === 1 ? 'bhSlideExitToLeft' : 'bhSlideExitToRight'} ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both`
              }}
            >
              {renderBannerContent(displayBanners[prevIndex])}
            </div>
          ) : null}

          <div
            key={`cur-${activeBannerKey}`}
            className="absolute inset-0 z-10"
            style={{
              willChange: 'transform',
              animation:
                prevIndex != null
                  ? `${direction === 1 ? 'bhSlideEnterFromRight' : 'bhSlideEnterFromLeft'} ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both`
                  : 'none'
            }}
          >
            {renderBannerContent(activeBanner)}
          </div>
        </>
      ) : null}

      {hasMultipleSlides && (
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <div className="flex">
            {displayBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="flex-1 h-[3px] bg-white/20 relative overflow-hidden"
                aria-label={`Go to slide ${index + 1}`}
              >
                <span
                  className="absolute inset-y-0 left-0"
                  style={{
                    width: index === currentIndex ? `${Math.min(100, Math.max(0, progress))}%` : index < currentIndex ? '100%' : '0%',
                    backgroundColor: PRIMARY
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerCarousel;
